import React, { useState, useMemo, useEffect } from "react";
import { formatTime } from "../utils/formatTime";
import { getRoleFlags } from "../hooks/useRole";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

export default function CalendarView({ jobs, currentUser, onSignup, onWithdraw }) {
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const { year, month } = currentDate;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const monthName = new Date(year, month).toLocaleString("default", { month: "long" });

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i).toLocaleString("default", { month: "long" })
  );
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 2 + i);

  const prevMonth = () => {
    setCurrentDate((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentDate((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const goToday = () => {
    const d = new Date();
    setCurrentDate({ year: d.getFullYear(), month: d.getMonth() });
  };

  const { canManageData: isAdmin, isOfficer } = getRoleFlags(currentUser?.role);

  const jobsByDate = useMemo(() => {
    const map = {};
    jobs
      .filter((j) => j.status === "active" && (isAdmin || !j.isPrivate))
      .forEach((j) => {
        if (!j.date) return;
        if (!map[j.date]) map[j.date] = [];
        map[j.date].push(j);
      });
    return map;
  }, [jobs, isAdmin]);

  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  const m = isMobile;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedDateStr = selectedDay
    ? `${year}-${String(month + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`
    : null;
  const selectedJobs = selectedDateStr ? (jobsByDate[selectedDateStr] || []) : [];

  return (
    <div>
      <h2 style={{ margin: "0 0 16px", fontSize: 24, fontWeight: 800, color: "#111827", letterSpacing: "-0.3px" }}>Calendar</h2>

      <div style={styles.header}>
        <button style={{ ...styles.navBtn, ...(m ? styles.navBtnMobile : {}) }} onClick={prevMonth}>&lt;</button>
        <div style={styles.headerCenter}>
          <select
            value={month}
            onChange={(e) => setCurrentDate((prev) => ({ ...prev, month: Number(e.target.value) }))}
            style={{ ...styles.selectBtn, ...(m ? styles.selectBtnMobile : {}) }}
            aria-label="Select month"
          >
            {monthNames.map((name, i) => (
              <option key={i} value={i}>{name}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setCurrentDate((prev) => ({ ...prev, year: Number(e.target.value) }))}
            style={{ ...styles.selectBtn, ...(m ? styles.selectBtnMobile : {}) }}
            aria-label="Select year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button style={{ ...styles.todayBtn, ...(m ? styles.todayBtnMobile : {}) }} onClick={goToday}>Today</button>
        </div>
        <button style={{ ...styles.navBtn, ...(m ? styles.navBtnMobile : {}) }} onClick={nextMonth}>&gt;</button>
      </div>

      <div style={styles.weekRow}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} style={styles.weekDay}>{d}</div>
        ))}
      </div>

      <div style={styles.grid}>
        {cells.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} style={{ ...styles.emptyCell, minHeight: m ? 52 : 70 }} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayJobs = jobsByDate[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSelected = day === selectedDay;

          return (
            <div
              key={day}
              style={{
                ...styles.cell,
                minHeight: m ? 52 : 70,
                padding: m ? 6 : 4,
                ...(isToday ? styles.todayCell : {}),
                ...(isSelected ? styles.selectedCell : {}),
                cursor: dayJobs.length > 0 ? "pointer" : "default",
              }}
              onClick={() => setSelectedDay(day === selectedDay ? null : day)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedDay(day === selectedDay ? null : day);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
            >
              <span style={{
                ...styles.dayNumber,
                fontSize: m ? 16 : 13,
                ...(isToday ? styles.todayNumber : {}),
              }}>
                {day}
              </span>
              {dayJobs.length > 0 && (
                <div style={styles.dots}>
                  {dayJobs.slice(0, 3).map((j, idx) => (
                    <span
                      key={idx}
                      style={{
                        ...styles.dot,
                        width: m ? 8 : 8,
                        height: m ? 8 : 8,
                        background:
                          (j.signups || []).length >= j.slotsTotal ? "#d97706" :
                          (j.signups || []).includes(currentUser.id) ? "#1d4ed8" : "#16a34a",
                      }}
                    />
                  ))}
                  {dayJobs.length > 3 && (
                    <span style={styles.moreText}>+{dayJobs.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.legend}>
        <span style={styles.legendItem}><span style={{ ...styles.dot, background: "#16a34a" }} /> Open</span>
        <span style={styles.legendItem}><span style={{ ...styles.dot, background: "#1d4ed8" }} /> Signed Up</span>
        <span style={styles.legendItem}><span style={{ ...styles.dot, background: "#d97706" }} /> Full</span>
      </div>

      {selectedDay && (
        <div style={styles.detailPanel}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>
            {monthName} {selectedDay}, {year}
            {selectedJobs.length > 0 && ` — ${selectedJobs.length} shift${selectedJobs.length > 1 ? "s" : ""}`}
          </h3>
          {selectedJobs.length === 0 ? (
            <p style={{ color: "#888", fontSize: 13 }}>No shifts scheduled.</p>
          ) : (
            selectedJobs.map((job) => {
              const isSignedUp = (job.signups || []).includes(currentUser.id);
              const isFull = (job.signups || []).length >= job.slotsTotal;
              return (
                <div key={job.id} style={styles.jobCard}>
                  <div style={styles.jobHeader}>
                    <strong>{job.title}</strong>
                    <span style={{
                      ...styles.badge,
                      background: isFull ? "rgba(217,119,6,0.08)" : "rgba(22,163,74,0.08)",
                      color: isFull ? "#d97706" : "#16a34a",
                    }}>
                      {isFull ? "Full" : "Open"}
                    </span>
                  </div>
                  <div style={styles.jobMeta}>
                    <span>{formatTime(job.startTime)} - {formatTime(job.endTime)}</span>
                    <span>{job.location}</span>
                    <span>${job.payRate}/hr</span>
                    <span>{(job.signups || []).length}/{job.slotsTotal} officers</span>
                  </div>
                  {isOfficer && (
                    <div style={{ marginTop: 8 }}>
                      {isSignedUp ? (
                        <button style={{ ...styles.actionBtn, background: "#dc2626" }} onClick={() => onWithdraw(job.id)}>
                          Withdraw
                        </button>
                      ) : !isFull && !(job.blockedOfficers || []).includes(currentUser.id) ? (
                        <button style={styles.actionBtn} onClick={() => onSignup(job.id)}>
                          Sign Up
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  headerCenter: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  selectBtn: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "6px 28px 6px 10px",
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    cursor: "pointer",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M3 5l3 3 3-3'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 8px center",
  },
  selectBtnMobile: {
    minHeight: 44,
    fontSize: 15,
    padding: "8px 30px 8px 12px",
  },
  navBtn: {
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  navBtnMobile: {
    minWidth: 44,
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  todayBtn: {
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  todayBtnMobile: {
    minWidth: 44,
    minHeight: 44,
    padding: "6px 14px",
    fontSize: 14,
  },
  weekRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
    marginBottom: 4,
  },
  weekDay: {
    textAlign: "center",
    fontFamily: "'Fira Code', monospace",
    fontSize: 11,
    fontWeight: 500,
    color: "#9ca3af",
    padding: 6,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
  },
  emptyCell: {
    minHeight: 70,
    background: "#f9fafb",
    borderRadius: 6,
  },
  cell: {
    minHeight: 70,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "background 0.15s",
  },
  todayCell: {
    borderColor: "#1d4ed8",
    borderWidth: 2,
  },
  selectedCell: {
    background: "rgba(29,78,216,0.06)",
    borderColor: "#1d4ed8",
    borderWidth: 2,
  },
  dayNumber: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    marginBottom: 4,
  },
  todayNumber: {
    background: "#1d4ed8",
    color: "#fff",
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
  },
  dots: {
    display: "flex",
    gap: 3,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
  },
  moreText: {
    fontSize: 10,
    color: "#9ca3af",
  },
  legend: {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    padding: "12px 0",
    fontSize: 12,
    color: "#6b7280",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  detailPanel: {
    marginTop: 12,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    padding: 16,
  },
  jobCard: {
    background: "#f9fafb",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  jobHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  badge: {
    fontFamily: "'Fira Code', monospace",
    padding: "3px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.3px",
  },
  jobMeta: {
    display: "flex",
    gap: 12,
    fontSize: 12,
    color: "#6b7280",
    flexWrap: "wrap",
  },
  actionBtn: {
    padding: "6px 14px",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
};
